import { Id } from "../../convex/_generated/dataModel";
import { HydratedEntry, HydratedLocation, HydratedPlant } from "../../convex/shared_util";
import { formatDate } from "./util";

export type DetailItemPlant = { plant: HydratedPlant };
export type DetailItemLocation = { location: HydratedLocation };
export type DetailItemEntry = { entry: HydratedEntry };

/**
 * One plant, location or entry — what a single detail drodal shows. The detail
 * stack holds refs rather than these, so what is on screen stays live; an item
 * is only ever a snapshot for first paint (see `useDetailItem`).
 */
export type DetailItemSingle = DetailItemPlant | DetailItemLocation | DetailItemEntry;

/** a list with one item active — the drodal gets prev/next arrows */
export type DetailItemList =
  | { plants: DetailItemPlant[], activeIndex: number }
  | { locations: DetailItemLocation[], activeIndex: number }
  | { entries: DetailItemEntry[], activeIndex: number };

/** what callers hand to `push` */
export type DetailItem = DetailItemSingle | DetailItemList;

export type DetailItemKind = "plant" | "location" | "entry";

/**
 * A reference to a detail item, e.g. `p-jd7f...`. This is also the value of the
 * `?v=` search param, so it is part of the URL contract — the prefixes can't
 * change without breaking existing links.
 */
export type DetailRef = string;

const KIND_BY_PREFIX: Record<string, DetailItemKind> = {
  p: "plant",
  l: "location",
  e: "entry",
};
const PREFIX_BY_KIND: Record<DetailItemKind, string> = {
  plant: "p",
  location: "l",
  entry: "e",
};

/**
 * @param item a detail item
 * @returns the ref for it
 */
export function itemRef(item: DetailItemSingle): DetailRef {
  if ("plant" in item) { return `${PREFIX_BY_KIND.plant}-${item.plant._id}`; }
  if ("location" in item) { return `${PREFIX_BY_KIND.location}-${item.location._id}`; }
  if ("entry" in item) { return `${PREFIX_BY_KIND.entry}-${item.entry._id}`; }
  throw new Error("Unknown detail item");
}

/**
 * @param item a detail item
 * @returns the title to show for it
 */
export function itemTitle(item: DetailItemSingle): string {
  if ("plant" in item) { return item.plant.name; }
  if ("location" in item) { return item.location.name; }
  if ("entry" in item) { return formatDate(item.entry.date); }
  throw new Error("Unknown detail item");
}

/**
 * Split a ref into its kind and id. Refs arrive from the URL, so the shape is
 * checked here — an id that is merely plausible can still be rejected by the
 * server, which surfaces as a thrown query and is caught by the detail error
 * boundary.
 * @param ref a ref string, or undefined
 * @returns the kind and id, or undefined if this isn't a usable ref
 */
export function parseDetailRef(ref: DetailRef | undefined):
  { kind: DetailItemKind; id: string } | undefined {
  if (!ref) { return undefined; }
  const separator = ref.indexOf("-");
  if (separator < 0) { return undefined; }
  const kind = KIND_BY_PREFIX[ref.slice(0, separator)];
  const id = ref.slice(separator + 1);
  if (!kind || !/^[A-Za-z0-9_-]{10,64}$/u.test(id)) { return undefined; }
  return { kind, id };
}

/**
 * @param ref a ref string, or undefined
 * @returns whether it is worth trying to resolve
 */
export function isDetailRef(ref: DetailRef | undefined): boolean {
  return parseDetailRef(ref) !== undefined;
}

/**
 * Narrow a parsed id to a table. The cast is unavoidable — the id comes from a
 * URL, and only the server can say whether it is real.
 * @param id the id part of a ref
 * @returns the same string, typed for the table
 */
export function asId<T extends "plants" | "locations" | "entries">(id: string): Id<T> {
  return id as Id<T>;
}
